import { useUserAuthentication } from '@quizzach/hooks';
import {
	CategoryFetchResponse,
	CategoryGeneratorCartRequest,
	CategoryGeneratorState,
	CustomizerRejectionType,
	getCategoryQuestionTotal,
	RoutePath,
	transformCategoryStateToCartRequest,
} from '@quizzach/interfaces';
import {
	consoleDev,
	ConsoleStatus,
	fetchData,
	readGeneratorSession,
} from '@quizzach/utils';
import { useNavigate } from 'react-router-dom';
import { useCartContext } from '@quizzach/contexts';

type AvailableQuestionKey = `available_questions_${1 | 2 | 3}`;

export const useQuizValidityChecker = () => {
	const navigate = useNavigate();
	const { user } = useUserAuthentication();
	const { refreshCartCount } = useCartContext();

	// runs during render — readGeneratorSession discards corrupt or
	// wrong-shaped storage instead of letting it crash the page
	const localQuizData: CategoryGeneratorState[] = readGeneratorSession();

	const mappedData: CategoryGeneratorCartRequest[] =
		transformCategoryStateToCartRequest(localQuizData);

	const initiateAddToCart = async () => {
		if (user) {
			return await addToCart();
		} else {
			// after login the user lands back on the configurator with their
			// selection restored (generatorQuiz sessionStorage) and continues
			// themselves
			navigate(RoutePath.Login, {
				state: { source: 'generator', google_referer: '/generator/customizer' },
			});
			return { status: false, redirectedToLogin: true };
		}
	};

	const addToCart = async () => {
		try {
			const cartResponse = await fetchData(
				'/api/cart/items',
				{
					method: 'POST',
					body: JSON.stringify({ configurations: mappedData }),
				},
				{ authRoute: true }
			);

			if (cartResponse.status < 300) {
				consoleDev('addToCart', ConsoleStatus.SUCC);
				sessionStorage.removeItem('generatorQuiz');
				refreshCartCount();

				return { status: true };
			}
			if (cartResponse.status === 409) {
				consoleDev(
					'addToLoggedInUserCart',
					ConsoleStatus.HTTP,
					cartResponse.status
				);
				const availabilityChanged = await checkWhereTo();
				// when the stored availability still matches the server, there is
				// nothing the user can fix — flag the conflict so the generic
				// "can't generate" message is shown
				return {
					status: false,
					rejectionType: availabilityChanged
						? CustomizerRejectionType.customizer
						: undefined,
					conflict: !availabilityChanged,
				};
			}
			return { status: false };
		} catch (err) {
			consoleDev('addToCart', ConsoleStatus.NET, err);
			return { status: false };
		}
	};

	// TODO: when logging out of quiz, we redirect user to login, after login to 404 page not found

	/**
	 * After a 409, decides whether the stored availability went stale (the user
	 * can fix their selection on the configurator, which re-fetches and re-syncs
	 * the session state) or the server genuinely can't build the quiz. Returns
	 * true when any category/subcategory disappeared or lost questions relative
	 * to what the user configured against.
	 */
	const checkWhereTo = async (): Promise<boolean> => {
		try {
			const response = await fetchData<CategoryGeneratorState[]>(
				`/api/categories`,
				{
					method: 'GET',
				},
				{
					authRoute: true,
				}
			);
			if (response.status >= 300) {
				consoleDev('checkWhereTo', ConsoleStatus.HTTP, response.status);
				return false;
			}
			if (!response.data) {
				return false;
			}

			// Map the categories returned by the API for quick lookup using category IDs
			const apiCategoriesMap = new Map(
				response.data.map((category) => [category.id, category])
			);

			for (const localCategory of localQuizData) {
				// a category contributing no questions was never submitted
				// (empty subcategory-mode selection) — its availability can't
				// have caused the 409, so don't let it trigger the
				// "fix your selection" flow
				const categoryCount = localCategory.question_count ?? 0;
				if (getCategoryQuestionTotal(localCategory) === 0) {
					continue;
				}

				const apiCategory = apiCategoriesMap.get(localCategory.id);

				// Category disappeared or ran out of questions
				if (!apiCategory || apiCategory.available_questions === 0) {
					return true;
				}

				// Fewer available questions in the category than when the user configured it
				if (
					apiCategory.available_questions < localCategory.available_questions
				) {
					return true;
				}

				// Compare per-level availability (1, 2, 3)
				for (let i = 1; i <= 3; i++) {
					const apiQuestions = apiCategory[
						`available_questions_${i}` as keyof CategoryFetchResponse
					] as number;
					const localQuestions = localCategory[
						`available_questions_${i}` as keyof CategoryGeneratorState
					] as number;

					if (apiQuestions < localQuestions) {
						return true;
					}
				}

				// Same checks per subcategory
				const subcategoriesMap = new Map(
					apiCategory.subcategories.map((sub) => [sub.id, sub])
				);

				for (const localSub of localCategory.subcategories) {
					// only validate subs that were part of the payload — the
					// mirror of transformCategoryStateToCartRequest's sub
					// filter: category mode drops explicit-0 subs, subcategory
					// mode drops everything unpicked
					const subWasSubmitted =
						categoryCount > 0
							? localSub.question_count !== 0
							: (localSub.question_count ?? 0) > 0;
					if (!subWasSubmitted) {
						continue;
					}
					const apiSub = subcategoriesMap.get(localSub.id);
					if (!apiSub || apiSub.available_questions === 0) {
						return true;
					}

					for (let i = 1; i <= 3; i++) {
						const apiSubQuestions = apiSub[
							`available_questions_${i}` as AvailableQuestionKey
						] as number;
						const localSubQuestions = localSub[
							`available_questions_${i}` as AvailableQuestionKey
						] as number;

						if (apiSubQuestions < localSubQuestions) {
							return true;
						}
					}
				}
			}
			return false;
		} catch (err) {
			consoleDev('checkWhereTo', ConsoleStatus.NET, err);
			return false;
		}
	};

	return { initiateAddToCart };
};
